import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { clearOfflineMail, registerServiceWorker } from '@/lib/pwa';
import { createQueryClient } from '@/lib/query-client';
import { initI18n, initTheme } from '@mailserver/web-shared';
import { onLogout, redeemHandoff } from '@mailserver/web-shared/session';
import { createAppRouter } from '@/router';
import './index.css';

/**
 * An administrator arriving from the admin panel carries a hand-off in the URL (ADR 0032).
 *
 * Spent **before the router is created**, because every route gates on a session and the redemption is
 * what creates one — starting the router first would bounce the arrival to the login screen and then
 * redeem into a page nobody is looking at.
 *
 * The token is stripped from the address bar either way. It is single-use and already spent, so what
 * is being removed is not a live credential but a confusing one: a URL that cannot be reloaded, and
 * that a screenshot or a pasted link would carry around forever.
 */
async function redeemHandoffFromUrl() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('handoff');
  if (!token) return;
  url.searchParams.delete('handoff');
  try {
    await redeemHandoff(token);
  } catch {
    // Expired, already spent, or the administrator lost the right to it. Nothing to recover: the app
    // carries on to its own sign-in, which is the truthful place to land.
  }
  window.history.replaceState(null, '', url.toString());
}

initTheme();
// Not awaited: the catalogue is a lazy import, and every string falls back to English until it
// lands. Blocking the first paint on a network round-trip to show the same words is a bad trade.
void initI18n();
registerServiceWorker();
// The offline mail cache is this app's, so emptying it on sign-out is this app's to ask for. The
// shared session used to import it directly, which made a mail client's cache a fact about every
// sign-out anywhere.
onLogout(() => void clearOfflineMail());
const queryClient = createQueryClient();
await redeemHandoffFromUrl();
const router = createAppRouter(queryClient);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
